import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { FilterInputProps } from "models/propTypes/wintFormPropsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
//#endregion
//#region components
import Icon from "../../icon";
import { Grid, CircularProgress, Autocomplete } from "@mui/material";
import BaseInput from "./baseInput";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { DialogEnums } from "models/enums";
import { FieldConstants, IconDefinitionConstants } from "utils/constants";

const FilterInput: React.FC<FilterInputProps> = (props) => {
  const itemNone: AutocompleteListItem = {
    value: UtilsHelper.getSelectionNoneText(),
  };

  const [inputValue, setInputValue] = React.useState(props.value ?? "");
  const [isValueFromOptions, setIsValueFromOptions] = React.useState<boolean>(true);

  const [options, setOptions] = React.useState<AutocompleteListItem[]>();
  const [currentAction, setCurrentAction] = React.useState<AutocompleteListItem>();

  const [open, setOpen] = React.useState<boolean>(false);
  const [focused, setFocused] = React.useState<boolean>(false);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const emptyResults: AutocompleteListItem[] = [
    {
      id: "notAvailable",
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NoResults),
      disabled: true,
    },
  ];

  const selectedItem: AutocompleteListItem | null = props.items.find((v) => v.id === props.value) ?? null;

  const onChange = (event: any, newValue: AutocompleteListItem | string | null) => {
    if (typeof newValue === "string") return;

    props.setValue(newValue?.id, true);
  };

  const inputChange = (value: string, reason: string) => {
    if (reason === "input") {
      const items = searchItems(value);
      setOptions(items);
    }
  };

  const getSuggestedItems: () => AutocompleteListItem[] = () => {
    const suggestedItems = props.includeNone ? [itemNone, ...(props.suggestedItems ?? [])] : props.suggestedItems;

    return suggestedItems?.length
      ? suggestedItems.map((x) => ({
          ...x,
          group: x.group ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
        }))
      : [];
  };

  const setDefaultOptions = () => {
    const items = searchItems("");

    if (
      JSON.stringify(
        (items ?? []).map((a) => {
          return { id: a.id, val: a.value };
        })
      ) !==
      JSON.stringify(
        (options ?? []).map((a) => {
          return { id: a.id, val: a.value };
        })
      )
    )
      setOptions(items);
  };

  const searchItems = (value: string): AutocompleteListItem[] => {
    let suggestedItems: AutocompleteListItem[] = [];

    if (!value) {
      suggestedItems = getSuggestedItems();
    }
    const items = props.includeNone ? [itemNone, ...(props.items ?? [])] : (props.items ?? []);
    let searchItems = value
      ? items
          .map((x) => ({
            index: x.value.toLowerCase().indexOf(value.toLowerCase()),
            item: x,
          }))
          .filter((x) => x.index >= 0)
          .sort((a, b) => a.index - b.index)
          .map((x) => x.item)
      : items;

    if (searchItems.length === 0) {
      searchItems = emptyResults;
    }
    return [...suggestedItems, ...searchItems];
  };

  const onInputChange = (event: object, value: string, reason: string) => {
    if (focused) {
      setOpen(true);
    } else {
      setOpen(false);
    }

    setIsValueFromOptions(reason === "reset");

    setInputValue(value);
    inputChange(value, reason);
  };

  const onOpen = () => {
    if (!focused) {
      setDefaultOptions();
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  const onFocus = () => {
    setFocused(true);
    setOpen(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setOpen(false);
    setTimeout(() => {
      props.onBlur(event);
    });
  };

  const onClick = () => {
    if (focused && !open) {
      setDefaultOptions();
      setOpen(true);
    }
  };

  const onActionClicked = (
    option: AutocompleteListItem,
    inputValue: string,
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (!option.enablePropagationOnActionClick) {
      event.stopPropagation();
    }
    setCurrentAction(option);

    if (option.form) {
      setOpen(false);
      dispatch(
        displayDialog({
          isOpen: false,
          id: option.title ?? "autoCompleteActionDialog",
          title: option.title ?? "",
          subTitle: option.subtitle ?? "",
          titleIcon: option.formIcon,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
            },
          ],
          children: option.form,
          onSubmit: (value: any) => {
            setOpen(false);
            setCurrentAction(undefined);
            props.setValue(value?.id, true);
          },
          onClose: () => {
            setFocused(true);
            setCurrentAction(undefined);
          },
          maxWidth: "sm",
        })
      );
    } else {
      option.onActionClicked &&
        option.onActionClicked(inputValue).then((res) => {
          if (res) {
            option.onActionSuccess && option.onActionSuccess(res);
            if (option.actionSuccessMessage) {
              setTimeout(() => {
                setActionResult(res);
              }, 500);
            } else {
              setActionResult(res);
            }
          }
        });
    }
  };

  const setActionResult = (result: AutocompleteListItem) => {
    setOpen(false);
    setCurrentAction(undefined);
    props.setValue(result?.id, true);
  };

  return (
    <Autocomplete
      id={`autoComplete-${props.name}`}
      options={(options ?? getSuggestedItems()).concat(props.actions ?? [])}
      getOptionLabel={(option) => (typeof option !== "string" ? `${option.value}` : "")}
      classes={{
        root: cssExports.autoCompleteRoot,
        popper: clsx(cssExports.autoCompletePopper, props.classes?.popper),
        paper: cssExports.autoCompleteContainer,
        option: cssExports.autoCompleteOption,
        groupLabel: cssExports.autoCompleteGroupTitle,
        noOptions: cssExports.autoCompleteNoOptions,
        inputRoot: clsx(
          props.disabled || props.success || props.hasError ? cssExports.autoCompleteInputWithEndAdornment : "",
          cssExports.autoCompleteInputRoot
        ),
        endAdornment: cssExports.autoCompleteEndAdornment,
        clearIndicator: cssExports.autoCompleteClearIndicator,
        popperDisablePortal: cssExports.popperDisabledPortal,
        popupIndicator: cssExports.autoCompletePopUpIndicator,
      }}
      loading
      loadingText={
        <Grid className={cssExports.actionItemContainer}>
          <Grid container direction="row" columnSpacing={1} className={cssExports.actionTitleOuter}>
            <Grid item>
              <CircularProgress size={20} className={cssExports.buttonProgressWhite} />
            </Grid>
            <Grid item className={cssExports.actionTitle}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Loading)}
            </Grid>
          </Grid>
        </Grid>
      }
      getOptionDisabled={(option) => !!option.disabled || option.id === "notAvailable"}
      value={selectedItem}
      open={open}
      blurOnSelect
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      onInputChange={onInputChange}
      disabled={props.disabled}
      renderOption={(elements, option, state) => {
        if (option.onActionClicked || option.form) {
          const selectedAction = currentAction && currentAction?.id === option.id;
          return (
            <li {...elements} data-testid={`autoCompleteOption-${option.id}`}>
              <Grid
                key={option.id}
                onClick={onActionClicked.bind(null, option, state.inputValue)}
                className={clsx(cssExports.actionItemContainer, option.className)}
              >
                <Grid container className={cssExports.actionTitleOuter}>
                  <Grid item>
                    {selectedAction ? (
                      <span className={clsx(cssExports.actionIcon, cssExports.success)}>
                        <Icon icon={IconDefinitionConstants.WCheck} />
                      </span>
                    ) : option.icon ? (
                      <span className={cssExports.actionIcon}>
                        <Icon icon={option.icon} />
                      </span>
                    ) : (
                      <></>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={cssExports.actionTitle}>
                      {currentAction?.actionSuccessMessage && selectedAction
                        ? currentAction.actionSuccessMessage
                        : option.value.replace(FieldConstants.ValueTemplateText, state.inputValue)}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </li>
          );
        }
        const matches = match(option.value, typeof inputValue === "string" ? inputValue : inputValue?.value);
        const parts = parse(option.value, matches);
        return (
          <li {...elements} data-testid={`autoCompleteOption-${option.value.replaceAll(" ", "-")}`}>
            <Grid key={option.id} container className={option.className}>
              <Grid container wrap="nowrap">
                {option.icon ? (
                  <span className={cssExports.icon}>
                    <Icon icon={option.icon} />
                  </span>
                ) : (
                  <></>
                )}
                <span className={clsx(cssExports.optionTextContainer, option.icon ? cssExports.hasIcon : "")}>
                  <span className={cssExports.optionText}>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        className={clsx(
                          part.highlight ? cssExports.highlightText : "",
                          option.description ? cssExports.withSubText : ""
                        )}
                      >
                        {part.text}
                      </span>
                    ))}
                  </span>
                  {option.description && <span className={cssExports.subText}>{option.description}</span>}
                </span>
              </Grid>
            </Grid>
          </li>
        );
      }}
      renderGroup={(option) => {
        return (
          <li key={option.key}>
            {!UtilsHelper.isEmpty(option.group) ? (
              <Grid container direction="row" className={cssExports.autoCompleteGroupTitle}>
                <Grid item xs>
                  {props.groupLabelFormatter ? props.groupLabelFormatter(option.group) : option.group}
                </Grid>
                {props.groupRightContent && props.groupRightContent(option.group)}
              </Grid>
            ) : (
              <></>
            )}
            <ul>{option.children}</ul>
          </li>
        );
      }}
      groupBy={(option) => (option.group === undefined ? "" : `${option.group}`)}
      filterOptions={(options) =>
        options.filter(
          (option) =>
            (!props.error || !option.hideWhenInvalid) &&
            (!option.hide ||
              !option.hide(
                options,
                typeof inputValue === "string" ? inputValue : inputValue?.value,
                isValueFromOptions
              ))
        )
      }
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <BaseInput
          type="search"
          className={props.className}
          name={props.name}
          icon={props.icon}
          value={props.value}
          label={props.label}
          setValue={() => {}}
          touched={props.touched}
          setTouched={props.setTouched}
          hasError={props.hasError}
          onChange={params.inputProps.onChange ?? (() => {})}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          inputProps={params.inputProps}
          InputProps={params.InputProps}
          optional={props.optional}
          disabled={props.disabled}
        />
      )}
      data-testid={`${props.name}FilterInput`}
    />
  );
};

export default FilterInput;
